import { useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Box, Button, Link as MuiLink, Stack, Typography } from '@mui/material'
import { formatDateDMY } from '@/utils/date'
import { ROUTES } from '@/utils/constants'
import { NewsArticleModal } from '@/components/news/NewsArticleModal'
import type { WidgetProps } from './WidgetProps'
import { EmptyHint } from './EmptyHint'

interface NewsRow {
  id: number
  title: string
  slug?: string | null
  preview: string | null
  published_at?: string | null
  created_at: string | null
}

/** В одну строку сетки помещается 5 новостей, в две и больше — 10 */
const ITEMS_ONE_ROW = 5
const ITEMS_TWO_ROWS = 10

export function NewsWidget({ payload, rows }: WidgetProps) {
  const { t } = useTranslation()
  const [openSlug, setOpenSlug] = useState<string | null>(null)

  const items = (payload.items ?? []) as NewsRow[]
  const visible = items.slice(0, rows >= 2 ? ITEMS_TWO_ROWS : ITEMS_ONE_ROW)

  if (items.length === 0) {
    return <EmptyHint fallbackKey="dashboard.widgets.news.empty" />
  }

  return (
    <Stack spacing={1.5} sx={{ height: '100%' }}>
      {visible.map((item) => (
        <Box key={item.id}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            {item.slug ? (
              <MuiLink
                component="a"
                href={ROUTES.NEWS_VIEW(item.slug)}
                onClick={(event: React.MouseEvent) => {
                  // Обычный клик — модалка на месте; Ctrl/⌘/средняя кнопка
                  // оставляют штатное поведение ссылки (новая вкладка, копирование)
                  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
                  event.preventDefault()
                  setOpenSlug(item.slug ?? null)
                }}
                underline="hover"
                variant="body2"
                fontWeight={600}
                color="text.primary"
                sx={{ flexGrow: 1, minWidth: 0, cursor: 'pointer' }}
                noWrap
              >
                {item.title}
              </MuiLink>
            ) : (
              <Typography variant="body2" fontWeight={600} sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
                {item.title}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {formatDateDMY(item.published_at ?? item.created_at)}
            </Typography>
          </Box>
          {item.preview && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {item.preview}
            </Typography>
          )}
        </Box>
      ))}

      <Box sx={{ mt: 'auto', pt: 0.5 }}>
        <Button component={RouterLink} to={ROUTES.NEWS} size="small" sx={{ textTransform: 'none' }}>
          {t('news.allNews')}
        </Button>
      </Box>

      <NewsArticleModal slug={openSlug} onClose={() => setOpenSlug(null)} />
    </Stack>
  )
}
